<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-14
-->
<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { CONTACT_INFO } from '~/constants/contact';
import { getLecturers } from '~/constants/lecturers';
import { PAGES_SEO } from '~/constants/seo';

const { t, locale } = useI18n();
const seoConfig = PAGES_SEO.contact;
const { trackPhone, trackAddress, trackSocial } = useAppTracking();

const handleContactLinkClick = (link: string, displayValue: string) => {
  if (link.startsWith('tel:')) {
    trackPhone(displayValue);
  } else if (link.startsWith('http') || link.includes('maps')) {
    trackAddress(displayValue);
  }
};

useSeoMeta({
  title: () => t(seoConfig.titleKey || ''),
  ogTitle: () => t(seoConfig.ogTitleKey || ''),
  description: () => t(seoConfig.descriptionKey || ''),
  ogDescription: () => t(seoConfig.ogDescriptionKey || ''),
  ogImage: seoConfig.ogImage,
  twitterCard: 'summary_large_image',
});

if (seoConfig.faqs) {
  useSchemaOrg([
    defineWebPage({
      '@type': 'FAQPage',
    }),
    ...seoConfig.faqs(t).map((faq) =>
      defineQuestion({
        name: faq.question,
        acceptedAnswer: faq.answer,
      })
    ),
  ]);
}

const isMounted = ref(false);
const hasSubmittedRecently = ref(false);

const checkSubmissionCooldown = () => {
  if (typeof window !== 'undefined') {
    const lastSub = localStorage.getItem('_esc_f_t');
    if (lastSub) {
      const diff = Date.now() - parseInt(lastSub, 10);
      if (diff < 86400000) {
        hasSubmittedRecently.value = true;
      }
    }
  }
};

onMounted(() => {
  isMounted.value = true;
  document.addEventListener('click', handleClickOutside);
  checkSubmissionCooldown();
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Contact info data
const contactInfo = computed(() => [
  {
    icon: 'lucide:phone-call',
    title: t('contact.info.phone'),
    values: CONTACT_INFO.phones.map((p) => p.display),
    links: CONTACT_INFO.phones.map((p) => `tel:${p.value}`),
  },
  {
    icon: 'lucide:mail',
    title: t('contact.info.email'),
    values: [CONTACT_INFO.email],
    links: [`mailto:${CONTACT_INFO.email}`],
  },
  {
    icon: 'lucide:map-pin',
    title: t('contact.info.address'),
    values: [t('footer.addressVal')],
    links: [CONTACT_INFO.googleMapsUrl],
  },
  {
    icon: 'lucide:clock',
    title: t('contact.info.hours'),
    values: [t('contact.info.hoursVal')],
    links: [],
  },
]);

// Social channels
const socialChannels = [
  {
    name: 'Facebook',
    icon: 'lucide:facebook',
    href: CONTACT_INFO.socials.facebook,
    color: 'hover:text-[#1877f2] hover:border-[#1877f2]',
  },
  {
    name: 'YouTube',
    icon: 'lucide:youtube',
    href: CONTACT_INFO.socials.youtube,
    color: 'hover:text-[#ff0000] hover:border-[#ff0000]',
  },
  {
    name: 'Telegram',
    icon: 'lucide:send',
    href: CONTACT_INFO.socials.telegram,
    color: 'hover:text-[#0088cc] hover:border-[#0088cc]',
  },
  {
    name: 'LinkedIn',
    icon: 'lucide:linkedin',
    href: CONTACT_INFO.socials.linkedin,
    color: 'hover:text-[#0a66c2] hover:border-[#0a66c2]',
  },
];

// Form state
const form = ref({
  name: '',
  email: '',
  phone: '',
  subject: 'General',
  message: '',
  token: '',
});

const isSubmitting = ref(false);
const isSuccess = ref(false);
const errorMessage = ref<string | null>(null);

// Turnstile reset reference
const turnstileRef = ref<{ reset: () => void } | null>(null);
// Form submission handler
const submitForm = async () => {
  if (!form.value.name || !form.value.phone || !form.value.message) {
    errorMessage.value = t('contact.validation.requiredFields');
    return;
  }

  if (!form.value.token) {
    errorMessage.value = t('contact.validation.turnstile');
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;

  try {
    const res = await $fetch('/api/contact', {
      method: 'POST',
      body: form.value,
    });

    if (res.success) {
      isSuccess.value = true;
      if (typeof window !== 'undefined') {
        localStorage.setItem('_esc_f_t', String(Date.now()));
      }
      // Reset form fields
      form.value = {
        name: '',
        email: '',
        phone: '',
        subject: 'General',
        message: '',
        token: '',
      };
    }
  } catch (err: unknown) {
    console.error('Submission failed:', err);
    const fetchError = err as { data?: { statusMessage?: string } };
    errorMessage.value = fetchError.data?.statusMessage || t('contact.form.error');
    // Reset Turnstile token on failure to let user try again
    if (turnstileRef.value) {
      turnstileRef.value.reset();
    }
  } finally {
    isSubmitting.value = false;
  }
};

// Lecturers list from old about page
const lecturers = computed(() => getLecturers(locale.value));
// Custom Subject Dropdown State & Logic
const isSubjectDropdownOpen = ref(false);
const subjects = computed(() => [
  { value: 'General', label: t('contact.form.subjectGeneral') },
  { value: 'Support', label: t('contact.form.subjectSupport') },
  { value: 'Partnership', label: t('contact.form.subjectPartnership') },
]);

const selectedSubjectLabel = computed(() => {
  const match = subjects.value.find((s) => s.value === form.value.subject);
  return match ? match.label : t('contact.form.subjectGeneral');
});

const toggleSubjectDropdown = () => {
  isSubjectDropdownOpen.value = !isSubjectDropdownOpen.value;
};

const selectSubject = (val: string) => {
  form.value.subject = val;
  isSubjectDropdownOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  const dropdownEl = document.getElementById('subject-dropdown-container');
  if (dropdownEl && !dropdownEl.contains(event.target as Node)) {
    isSubjectDropdownOpen.value = false;
  }
};
</script>

<template>
  <div
    class="flex min-h-screen flex-col bg-slate-50 font-sans text-slate-800 selection:bg-brand-red selection:text-white"
  >
    <!-- Header -->
    <HomeHeader />

    <!-- Header Spacer to prevent layout shift with fixed header -->
    <div class="h-18 w-full shrink-0 md:h-31"></div>

    <!-- Page Hero Banner -->
    <section class="w-full border-b border-gray-200/50 bg-bg-gray py-12 md:py-16">
      <div class="mx-auto max-w-360 px-6 md:px-20">
        <!-- Breadcrumbs -->
        <nav class="flex items-center gap-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
          <NuxtLink to="/" class="transition hover:text-brand-blue">
            {{ t('home') }}
          </NuxtLink>
          <span class="text-slate-400">/</span>
          <span class="text-slate-800">{{ t('contact.hero.badge') }}</span>
        </nav>

        <h1 class="mt-4 font-sans text-3xl font-extrabold tracking-tight text-gray-900 md:text-5xl">
          {{ t('contact.hero.title') }}
        </h1>
        <p class="mt-3 max-w-3xl text-sm leading-relaxed text-gray-600 md:text-base">
          {{ t('contact.hero.subtitle') }}
        </p>
      </div>
    </section>

    <!-- Main Content Area -->
    <main class="mx-auto w-full max-w-360 flex-1 px-6 py-12 md:px-20">
      <!-- 1. Contact Information Cards Grid -->
      <section class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div
          v-for="(card, index) in contactInfo"
          :key="index"
          class="group flex flex-col items-center rounded-2xl border border-gray-100 bg-white p-6 text-center shadow-xs transition-all duration-300 hover:border-brand-blue/35 hover:shadow-md"
        >
          <!-- Card Icon with Ring effect -->
          <div
            class="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue/5 text-brand-blue transition-all duration-300 group-hover:bg-brand-blue group-hover:text-white"
          >
            <Icon :name="card.icon" class="h-5 w-5" />
          </div>

          <h3 class="mt-4 text-sm font-bold tracking-wider text-gray-950 uppercase">
            {{ card.title }}
          </h3>

          <div class="mt-3 flex flex-1 flex-col justify-center space-y-1">
            <template v-if="card.links.length > 0">
              <a
                v-for="(val, i) in card.values"
                :key="i"
                :href="card.links[Math.min(i, card.links.length - 1)]"
                :target="card.links[0]?.startsWith('http') ? '_blank' : undefined"
                class="text-sm font-medium break-all text-gray-600 transition hover:text-brand-blue"
                @click="handleContactLinkClick(card.links[Math.min(i, card.links.length - 1)] || '', val)"
              >
                {{ val }}
              </a>
            </template>
            <template v-else>
              <p v-for="(val, i) in card.values" :key="i" class="text-sm leading-relaxed font-medium text-gray-600">
                {{ val }}
              </p>
            </template>
          </div>
        </div>
      </section>

      <!-- 2. Form & Map Section Grid -->
      <section class="mt-12 grid grid-cols-1 gap-8 md:mt-16 md:gap-12 lg:grid-cols-12">
        <!-- Form Column -->
        <div
          class="relative flex flex-col justify-between overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-xs md:p-8 lg:col-span-7"
        >
          <div>
            <h2 class="flex items-center gap-2 text-xl font-bold text-gray-900 md:text-2xl">
              <Icon name="lucide:message-square" class="h-6 w-6 text-brand-blue" />
              {{ t('contact.form.title') }}
            </h2>
            <p class="mt-2 text-sm text-gray-500">
              {{ t('contact.form.subtitle') }}
            </p>

            <!-- Success Overlay -->
            <Transition name="fade">
              <div
                v-if="isSuccess"
                class="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/95 p-6 text-center"
              >
                <div
                  class="mb-4 flex h-16 w-16 animate-bounce items-center justify-center rounded-full bg-emerald-100 text-emerald-600"
                >
                  <Icon name="lucide:check-circle" class="h-10 w-10" />
                </div>
                <h3 class="text-xl font-bold text-gray-900">
                  {{ t('contact.form.successTitle') }}
                </h3>
                <p class="mt-2 max-w-sm text-sm text-gray-500">
                  {{ t('contact.form.successDesc') }}
                </p>
                <button
                  class="mt-6 rounded-full bg-brand-blue px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-blue/90"
                  @click="isSuccess = false"
                >
                  {{ locale === 'kh' ? 'ផ្ញើសារថ្មី' : 'Send another message' }}
                </button>
              </div>
            </Transition>

            <!-- Already Submitted Cooldown Card -->
            <div
              v-if="hasSubmittedRecently"
              class="animate-fade-in mt-8 flex flex-col items-center justify-center py-12 text-center"
            >
              <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-brand-blue">
                <Icon name="lucide:mail-check" class="h-8 w-8" />
              </div>
              <h3 class="text-xl font-bold text-gray-900">
                {{ locale === 'kh' ? 'សាររបស់អ្នកត្រូវបានផ្ញើរួចហើយ' : 'Message Already Submitted' }}
              </h3>
              <p class="mt-3 max-w-sm text-sm leading-relaxed text-gray-500">
                {{
                  locale === 'kh'
                    ? 'អ្នកបានផ្ញើសារមកកាន់យើងរួចរាល់ហើយសម្រាប់ថ្ងៃនេះ។ ប្រសិនបើមានការប្រញាប់ សូមទាក់ទងមកយើងខ្ញុំតាមរយៈលេខទូរស័ព្ទ ឬ Telegram ខាងក្រោម។'
                    : 'You have already sent us a contact message today. Our team is processing it. If your request is urgent, please call us or message us on Telegram.'
                }}
              </p>
              <button
                type="button"
                class="mt-8 rounded-full border border-gray-200 bg-white px-6 py-2 text-xs font-semibold text-slate-500 shadow-xs transition hover:bg-slate-50 hover:text-brand-blue"
                @click="hasSubmittedRecently = false"
              >
                {{ locale === 'kh' ? 'ផ្ញើសារថ្មីមួយទៀត' : 'Send another message anyway' }}
              </button>
            </div>

            <!-- Contact Form fields -->
            <form v-else class="mt-6 space-y-4" @submit.prevent="submitForm">
              <!-- Grid for Name & Phone -->
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <!-- Name -->
                <div class="flex flex-col">
                  <label
                    class="mb-1.5 flex items-center gap-1 text-xs font-bold tracking-wider text-gray-700 uppercase"
                  >
                    {{ t('contact.form.nameLabel') }} <span class="text-red-500">*</span>
                  </label>
                  <div class="relative">
                    <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                      <Icon name="lucide:user" class="h-4 w-4" />
                    </span>
                    <input
                      v-model="form.name"
                      type="text"
                      required
                      :placeholder="t('contact.form.namePlaceholder')"
                      class="w-full rounded-xl border border-gray-200 py-2.5 pr-4 pl-10 text-sm transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 focus:outline-none"
                    />
                  </div>
                </div>

                <!-- Phone -->
                <div class="flex flex-col">
                  <label
                    class="mb-1.5 flex items-center gap-1 text-xs font-bold tracking-wider text-gray-700 uppercase"
                  >
                    {{ t('contact.form.phoneLabel') }} <span class="text-red-500">*</span>
                  </label>
                  <div class="relative">
                    <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                      <Icon name="lucide:phone" class="h-4 w-4" />
                    </span>
                    <input
                      v-model="form.phone"
                      type="text"
                      required
                      :placeholder="t('contact.form.phonePlaceholder')"
                      class="w-full rounded-xl border border-gray-200 py-2.5 pr-4 pl-10 text-sm transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <!-- Grid for Email & Subject -->
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <!-- Email -->
                <div class="flex flex-col">
                  <label class="mb-1.5 text-xs font-bold tracking-wider text-gray-700 uppercase">
                    {{ t('contact.form.emailLabel') }}
                  </label>
                  <div class="relative">
                    <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                      <Icon name="lucide:mail" class="h-4 w-4" />
                    </span>
                    <input
                      v-model="form.email"
                      type="email"
                      :placeholder="t('contact.form.emailPlaceholder')"
                      class="w-full rounded-xl border border-gray-200 py-2.5 pr-4 pl-10 text-sm transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 focus:outline-none"
                    />
                  </div>
                </div>

                <!-- Subject -->
                <div id="subject-dropdown-container" class="flex flex-col">
                  <label class="mb-1.5 text-xs font-bold tracking-wider text-gray-700 uppercase">
                    {{ t('contact.form.subjectLabel') }}
                  </label>
                  <div class="relative">
                    <!-- Trigger Button -->
                    <button
                      type="button"
                      class="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white py-2.5 pr-4 pl-10 text-left text-sm transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 focus:outline-none"
                      @click="toggleSubjectDropdown"
                    >
                      <span class="absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                        <Icon name="lucide:message-square" class="h-4 w-4" />
                      </span>
                      <span class="text-slate-800">
                        {{ selectedSubjectLabel }}
                      </span>
                      <Icon
                        name="lucide:chevron-down"
                        class="h-4 w-4 text-gray-400 transition-transform duration-200"
                        :class="{ 'rotate-180': isSubjectDropdownOpen }"
                      />
                    </button>

                    <!-- Options Dropdown Menu -->
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-from-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-from-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0"
                    >
                      <div
                        v-show="isSubjectDropdownOpen"
                        class="absolute right-0 z-50 mt-1.5 max-h-60 w-full origin-top-right overflow-y-auto rounded-xl border border-gray-100 bg-white p-1.5 shadow-lg ring-1 ring-black/5 focus:outline-none"
                      >
                        <button
                          v-for="sub in subjects"
                          :key="sub.value"
                          type="button"
                          class="flex w-full items-center justify-between rounded-lg px-3.5 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50 hover:text-slate-900"
                          :class="{ 'bg-slate-50 font-bold text-brand-blue': form.subject === sub.value }"
                          @click="selectSubject(sub.value)"
                        >
                          <span>{{ sub.label }}</span>
                          <Icon v-if="form.subject === sub.value" name="lucide:check" class="h-4 w-4 text-brand-blue" />
                        </button>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>

              <!-- Message -->
              <div class="flex flex-col">
                <label class="mb-1.5 flex items-center gap-1 text-xs font-bold tracking-wider text-gray-700 uppercase">
                  {{ t('contact.form.messageLabel') }} <span class="text-red-500">*</span>
                </label>
                <textarea
                  v-model="form.message"
                  rows="4"
                  required
                  :placeholder="t('contact.form.messagePlaceholder')"
                  class="w-full resize-none rounded-xl border border-gray-200 px-4 py-2.5 text-sm transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 focus:outline-none"
                ></textarea>
              </div>

              <!-- Error feedback -->
              <div
                v-if="errorMessage"
                class="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-medium text-red-600"
              >
                <Icon name="lucide:alert-circle" class="h-4 w-4 shrink-0" />
                <span>{{ errorMessage }}</span>
              </div>

              <!-- Turnstile Spam Protection -->
              <div class="pt-1">
                <NuxtTurnstile ref="turnstileRef" v-model="form.token" :options="{ theme: 'light', size: 'normal' }" />
              </div>

              <!-- Submit button -->
              <button
                type="submit"
                :disabled="isSubmitting"
                class="btn-3d group mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-black/10 bg-brand-blue px-6 py-3 text-sm font-semibold text-white focus:ring-2 focus:ring-brand-blue/20 focus:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                style="--btn-3d-shadow: rgba(0, 0, 0, 0.35)"
              >
                <template v-if="isSubmitting">
                  <Icon name="lucide:loader-2" class="h-4 w-4 animate-spin" />
                  <span>{{ t('contact.form.submitting') }}</span>
                </template>
                <template v-else>
                  <Icon
                    name="lucide:send"
                    class="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-0.5"
                  />
                  <span>{{ t('contact.form.submit') }}</span>
                </template>
              </button>
            </form>
          </div>
        </div>

        <!-- Map Column -->
        <div class="flex flex-col gap-6 lg:col-span-5">
          <!-- Interactive Map Container -->
          <div class="flex grow flex-col rounded-2xl border border-gray-100 bg-white p-5 shadow-xs">
            <div class="mb-4 flex items-center justify-between">
              <h3 class="flex items-center gap-1.5 font-sans text-sm font-bold text-gray-900">
                <Icon name="lucide:map" class="h-4.5 w-4.5 text-brand-blue" />
                {{ locale === 'kh' ? 'ទីតាំងលើផែនទី' : 'Map View' }}
              </h3>
            </div>

            <!-- Google Maps Embed Iframe -->
            <div
              class="relative min-h-[280px] flex-1 overflow-hidden rounded-xl border border-gray-100 bg-slate-100 shadow-inner"
            >
              <iframe
                src="https://maps.google.com/maps?q=E-School%20Cambodia&t=&z=16&ie=UTF8&iwloc=&output=embed"
                class="absolute inset-0 h-full w-full border-0"
                allowfullscreen="true"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            <!-- External Navigation Link -->
            <a
              :href="CONTACT_INFO.googleMapsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="btn-3d mt-4 flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-gray-900 px-4 py-3 text-xs font-semibold text-white"
              style="--btn-3d-shadow: rgba(0, 0, 0, 0.4)"
              @click="trackAddress('Google Maps Button')"
            >
              <Icon name="lucide:navigation" class="h-3.5 w-3.5 shrink-0" />
              <span>{{ locale === 'kh' ? 'បើកក្នុង Google Maps' : 'Open in Google Maps' }}</span>
            </a>
          </div>
        </div>
      </section>

      <!-- 3. Who We Are Section (Preserving old contact page content) -->
      <section class="mt-16 border-t border-gray-200/50 pt-12 md:mt-24 md:pt-16">
        <div class="mx-auto max-w-3xl text-center">
          <h2 class="font-sans text-2xl font-extrabold text-gray-900 md:text-3xl">
            {{ t('contact.about.title') }}
          </h2>
          <p class="mt-4 text-sm leading-relaxed text-gray-600 md:text-base">
            {{ t('contact.about.desc') }}
          </p>
        </div>

        <!-- Lecturers grid display -->
        <div class="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="(lecturer, index) in lecturers"
            :key="index"
            class="flex flex-col items-center rounded-2xl border border-gray-100 bg-white p-5 text-center shadow-xs transition-all duration-300 hover:shadow-md"
          >
            <!-- Circular avatar with profile picture -->
            <div
              class="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full text-base font-bold text-white shadow-inner"
              :class="lecturer.bgColor"
            >
              <img
                v-if="lecturer.image"
                :src="lecturer.image"
                :alt="lecturer.name"
                class="absolute inset-0 h-full w-full rounded-full object-cover transition-opacity duration-300"
              />
              <span v-else>{{ lecturer.initials }}</span>
            </div>

            <!-- Badge -->
            <span
              v-if="lecturer.prefix"
              class="mt-2.5 rounded-full border border-violet-200/50 bg-violet-50 px-2 py-px text-[8px] font-bold tracking-wider text-violet-600 uppercase sm:text-[9px]"
            >
              {{ lecturer.prefix }}
            </span>

            <h4 class="mt-2 text-sm font-bold text-gray-950">
              {{ lecturer.nameOnly }}
            </h4>
            <p class="mt-1 text-xs leading-normal text-gray-500">
              {{ lecturer.subject }}
            </p>
          </div>
        </div>
      </section>

      <!-- 4. Social & Connect Section -->
      <section class="mt-16 rounded-3xl border border-brand-blue/10 bg-brand-blue/5 p-8 text-center md:mt-24 md:p-12">
        <h3 class="text-lg font-bold tracking-wider text-brand-blue uppercase md:text-xl">
          {{ t('contact.info.socials') }}
        </h3>
        <p class="mx-auto mt-2 max-w-md text-xs text-gray-600 md:text-sm">
          {{ t('contact.social.desc') }}
        </p>

        <div class="mt-6 flex flex-wrap items-center justify-center gap-4">
          <a
            v-for="social in socialChannels"
            :key="social.name"
            :href="social.href"
            target="_blank"
            class="btn-3d-light inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-xs font-bold text-gray-700 md:text-sm"
            :class="social.color"
            @click="trackSocial(social.name, social.href)"
          >
            <Icon :name="social.icon" class="h-4 w-4" />
            {{ social.name }}
          </a>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <HomeFooter />
  </div>
</template>

<style>
/* Leaflet custom map resets */
.leaflet-popup-content-wrapper {
  background: white !important;
  border-radius: 12px !important;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid #f3f4f6 !important;
}
.leaflet-popup-tip {
  background: white !important;
}
.leaflet-container a.leaflet-popup-close-button {
  color: #9ca3af !important;
  top: 6px !important;
  right: 6px !important;
}
.leaflet-container .leaflet-control-attribution {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(4px);
  color: #6b7280 !important;
  font-size: 9px !important;
}
.leaflet-container .leaflet-control-attribution a {
  color: #045174 !important;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
